import { ANTIDOTE_ROUNDS, CARD_BY_ID, CARD_MAGNITUDE, TARGETED_CARDS } from './cards';
import { resolveDice } from './combat';
import {
  activePlayers,
  clone,
  areAdjacent,
  connectedThroughOwned,
  currentPlayer,
  frontierOf,
  isPinned,
  ownedBy,
  playerById,
  reinforcementsFor,
  territoryDef,
  totalZombies,
} from './helpers';
import { hordeRoundEnd, normalizeHorde, playerRising } from './rising';
import { pick, shuffle } from './rng';
import { drawsFor, escalation, rulesFor } from './rules';
import type {
  Action,
  CardId,
  GameEvent,
  GameState,
  MapDef,
  PlayerId,
  ReduceResult,
  TerritoryId,
} from './types';

type Ctx = { map: MapDef; state: GameState; events: GameEvent[] };

function fail(error: string): ReduceResult {
  return { ok: false, error };
}

/**
 * Apply one action. Never mutates the input state; returns a new state and the
 * events that describe what happened. Invalid actions return `{ ok: false }`.
 */
export function reduce(map: MapDef, input: GameState, action: Action): ReduceResult {
  if (input.outcome) return fail('The game is over');
  const current = currentPlayer(input);
  if (action.playerId !== current.id) return fail(`It is ${current.id}'s turn`);

  const state = clone(input);
  const ctx: Ctx = { map, state, events: [] };

  const error = apply(ctx, action);
  if (error) return fail(error);
  return { ok: true, state, events: ctx.events };
}

function apply(ctx: Ctx, action: Action): string | null {
  const { state } = ctx;
  if (state.pendingOccupy && action.type !== 'occupy') {
    return 'Move armies into the captured territory first';
  }
  switch (action.type) {
    case 'place':
      return place(ctx, action.territoryId, action.count);
    case 'drawCard':
      return drawCard(ctx);
    case 'playCard':
      return playCard(ctx, action.cardId, action.target);
    case 'attack':
      return attack(ctx, action.from, action.to, action.dice);
    case 'occupy':
      return occupy(ctx, action.count);
    case 'endAttack':
      if (state.phase !== 'attack') return 'Not in the attack phase';
      setPhase(ctx, 'fortify');
      return null;
    case 'fortify':
      return fortify(ctx, action.from, action.to, action.count);
    case 'endTurn':
      if (state.phase !== 'attack' && state.phase !== 'fortify') return 'Cannot end the turn now';
      endTurn(ctx);
      return null;
    default:
      // Input can come from the network: never let an unknown type through as a no-op.
      return 'Unknown action';
  }
}

// ---------------------------------------------------------------------------

function setPhase(ctx: Ctx, phase: GameState['phase']): void {
  ctx.state.phase = phase;
  ctx.events.push({
    type: 'phaseChanged',
    phase,
    playerId: phase === 'ended' ? null : currentPlayer(ctx.state).id,
    round: ctx.state.round,
  });
}

function enterDraw(ctx: Ctx): void {
  ctx.state.drawsPending = drawsFor(rulesFor(ctx.state.config.difficulty), ctx.state.round);
  setPhase(ctx, 'draw');
}

function place(ctx: Ctx, territoryId: TerritoryId, count: number): string | null {
  const { state } = ctx;
  const me = currentPlayer(state).id;
  if (state.phase !== 'reinforce' && state.phase !== 'draw') return 'Not placing units now';
  if (state.reinforcementsPending <= 0) return 'No units to place';
  if (!Number.isInteger(count) || count < 1 || count > state.reinforcementsPending) {
    return `Place between 1 and ${state.reinforcementsPending} units`;
  }
  const t = state.territories[territoryId];
  if (!t || t.owner !== me) return 'You can only reinforce territories you own';
  t.armies += count;
  state.reinforcementsPending -= count;
  ctx.events.push({ type: 'reinforced', playerId: me, territoryId, count });
  if (state.reinforcementsPending === 0) {
    if (state.phase === 'reinforce') enterDraw(ctx);
    else if (state.drawsPending === 0) setPhase(ctx, 'attack');
  }
  return null;
}

function drawCard(ctx: Ctx): string | null {
  const { state } = ctx;
  const me = currentPlayer(state);
  if (state.phase !== 'draw') return 'Not in the draw phase';
  if (state.reinforcementsPending > 0) return 'Place your units first';
  if (state.drawsPending <= 0) return 'No cards to draw';

  if (state.deck.length === 0) {
    if (state.discard.length === 0) return 'The deck is empty';
    [state.deck, state.rng] = shuffle(state.rng, state.discard);
    state.discard = [];
  }
  const cardId = state.deck.shift()!;
  const card = CARD_BY_ID[cardId]!;
  ctx.events.push({ type: 'cardDrawn', playerId: me.id, cardId, kind: card.kind });
  state.drawsPending -= 1;

  if (card.kind === 'instant') {
    resolveInstant(ctx, cardId);
    state.discard.push(cardId);
    ctx.events.push({ type: 'cardResolved', playerId: me.id, cardId });
  } else if (me.hand.length >= rulesFor(state.config.difficulty).handLimit) {
    state.discard.push(cardId);
    ctx.events.push({ type: 'cardDiscarded', playerId: me.id, cardId, reason: 'handFull' });
  } else {
    me.hand.push(cardId);
  }

  checkEliminations(ctx);
  if (checkOutcome(ctx)) return null;
  if (state.drawsPending === 0 && state.reinforcementsPending === 0) setPhase(ctx, 'attack');
  return null;
}

function resolveInstant(ctx: Ctx, cardId: CardId): void {
  const { state, map } = ctx;
  const me = currentPlayer(state).id;
  const m = escalation(rulesFor(state.config.difficulty), state.round);
  const owned = ownedBy(state, me).sort();

  switch (cardId) {
    case 'quarantine':
      state.modifiers.quarantineUntilRound = state.round + 2;
      return;
    case 'reinforcements':
      state.reinforcementsPending += CARD_MAGNITUDE['reinforcements']! * m;
      return;
    case 'outbreak': {
      if (owned.length === 0) return;
      const infested = owned.filter((id) => state.territories[id]!.zombies > 0);
      let id: TerritoryId;
      [id, state.rng] = pick(state.rng, infested.length > 0 ? infested : owned);
      const t = state.territories[id]!;
      const added = Math.min(Math.max(2, t.zombies), CARD_MAGNITUDE['outbreak']! * m);
      t.zombies += added;
      ctx.events.push({ type: 'zombiesSpawned', territoryId: id, count: added });
      return;
    }
    case 'contaminated-supplies': {
      if (owned.length === 0) return;
      const largest = owned.reduce((a, b) =>
        state.territories[b]!.armies > state.territories[a]!.armies ? b : a,
      );
      const t = state.territories[largest]!;
      const lost = Math.min(CARD_MAGNITUDE['contaminated-supplies']! * m, t.armies - 1);
      if (lost <= 0) return;
      t.armies -= lost;
      ctx.events.push({ type: 'armiesLost', territoryId: largest, count: lost });
      return;
    }
    case 'migration': {
      const sources = frontierOf(state, map, me)
        .filter((id) => state.territories[id]!.zombies > 0)
        .sort();
      if (sources.length === 0) return;
      let from: TerritoryId;
      [from, state.rng] = pick(state.rng, sources);
      const targets = territoryDef(map, from).neighbors.filter(
        (n) => state.territories[n]!.owner === me,
      );
      let to: TerritoryId;
      [to, state.rng] = pick(state.rng, targets);
      const src = state.territories[from]!;
      const count = src.zombies;
      src.zombies = 0;
      state.territories[to]!.zombies += count;
      normalizeHorde(state, from);
      ctx.events.push({ type: 'zombiesMoved', from, to, count });
      return;
    }
    case 'mutation':
      state.modifiers.mutationSteps += 1;
      state.modifiers.mutationUntilRound = state.round + 2;
      return;
    case 'siren':
      if (!state.modifiers.sirenPlayers.includes(me)) state.modifiers.sirenPlayers.push(me);
      return;
    default:
      throw new Error(`Unknown instant card ${cardId}`);
  }
}

function playCard(ctx: Ctx, cardId: CardId, target: TerritoryId | undefined): string | null {
  const { state, map } = ctx;
  const me = currentPlayer(state);
  if (state.phase === 'ended') return 'The game is over';
  const idx = me.hand.indexOf(cardId);
  if (idx < 0) return 'That card is not in your hand';
  const m = escalation(rulesFor(state.config.difficulty), state.round);

  if (TARGETED_CARDS.has(cardId) && !target) return 'This card needs a target';
  const t = target ? state.territories[target] : undefined;
  if (target && !t) return `Unknown territory ${target}`;

  switch (cardId) {
    case 'cleanup-crew': {
      if (t!.owner !== me.id) return 'Cleanup Crew targets a territory you own';
      if (t!.zombies === 0) return 'No zombies there';
      const removed = Math.min(CARD_MAGNITUDE['cleanup-crew']! * m, t!.zombies);
      t!.zombies -= removed;
      ctx.events.push({ type: 'zombiesRemoved', territoryId: target!, count: removed });
      break;
    }
    case 'airstrike': {
      const ok = t!.owner === me.id || frontierOf(state, map, me.id).includes(target!);
      if (!ok) return 'Airstrike targets a territory you own or border';
      if (t!.zombies === 0) return 'No zombies there';
      const removed = Math.min(CARD_MAGNITUDE['airstrike']! * m, t!.zombies);
      t!.zombies -= removed;
      normalizeHorde(state, target!);
      ctx.events.push({ type: 'zombiesRemoved', territoryId: target!, count: removed });
      break;
    }
    case 'antidote':
      if (t!.owner !== me.id) return 'Antidote targets a territory you own';
      t!.immuneUntilRound = state.round + ANTIDOTE_ROUNDS;
      break;
    default:
      return `${cardId} cannot be played from hand`;
  }

  me.hand.splice(idx, 1);
  state.discard.push(cardId);
  ctx.events.push(
    target
      ? { type: 'cardResolved', playerId: me.id, cardId, target }
      : { type: 'cardResolved', playerId: me.id, cardId },
  );
  checkOutcome(ctx);
  return null;
}

function attack(ctx: Ctx, fromId: TerritoryId, toId: TerritoryId, dice: number): string | null {
  const { state, map } = ctx;
  const me = currentPlayer(state).id;
  if (state.phase !== 'attack') return 'Not in the attack phase';
  const from = state.territories[fromId];
  const to = state.territories[toId];
  if (!from || !to) return 'Unknown territory';
  if (from.owner !== me) return 'You can only attack from territories you own';
  if (![1, 2, 3].includes(dice)) return 'Roll 1 to 3 dice';

  const purge = fromId === toId;
  let defenders: number;
  if (purge) {
    if (from.zombies === 0) return 'No zombies to purge';
    if (from.armies < 1) return 'No armies to purge with';
    if (dice > Math.min(3, from.armies)) return 'Not enough armies for that many dice';
    defenders = from.zombies;
  } else {
    if (!areAdjacent(map, fromId, toId)) return 'Territories are not adjacent';
    if (to.owner === me) return 'You already own that territory';
    if (from.armies < 2) return 'Need at least 2 armies to attack';
    if (isPinned(state, fromId)) return 'Pinned: zombies outnumber your armies there';
    if (dice > Math.min(3, from.armies - 1)) return 'Not enough armies for that many dice';
    defenders = to.owner === 'horde' || to.owner === null ? to.zombies : to.armies;
  }

  if (defenders === 0) {
    capture(ctx, fromId, toId, 1);
    return null;
  }

  let outcome;
  [outcome, state.rng] = resolveDice(state.rng, dice, Math.min(2, defenders));
  ctx.events.push({ type: 'diceRolled', from: fromId, to: toId, ...outcome });
  from.armies -= outcome.attackerLosses;

  if (purge) {
    from.zombies -= outcome.defenderLosses;
    if (outcome.defenderLosses > 0) {
      ctx.events.push({
        type: 'zombiesRemoved',
        territoryId: fromId,
        count: outcome.defenderLosses,
      });
    }
    if (from.armies === 0 && from.zombies > 0) {
      ctx.events.push({ type: 'overrun', territoryId: fromId, previousOwner: me });
      from.owner = 'horde';
      checkEliminations(ctx);
    }
    checkOutcome(ctx);
    return null;
  }

  if (to.owner === 'horde') {
    to.zombies -= outcome.defenderLosses;
    if (outcome.defenderLosses > 0) {
      ctx.events.push({ type: 'zombiesRemoved', territoryId: toId, count: outcome.defenderLosses });
    }
    if (to.zombies === 0) capture(ctx, fromId, toId, dice);
  } else {
    to.armies -= outcome.defenderLosses;
    if (outcome.defenderLosses > 0) {
      ctx.events.push({ type: 'armiesLost', territoryId: toId, count: outcome.defenderLosses });
    }
    if (to.armies === 0) capture(ctx, fromId, toId, dice);
  }
  return null;
}

function capture(ctx: Ctx, fromId: TerritoryId, toId: TerritoryId, dice: number): void {
  const { state } = ctx;
  const me = currentPlayer(state).id;
  const from = state.territories[fromId]!;
  const to = state.territories[toId]!;
  const previous = to.owner;
  to.owner = me;
  to.armies = 0;
  ctx.events.push({ type: 'territoryCaptured', territoryId: toId, from: previous, to: me });
  state.pendingOccupy = {
    from: fromId,
    to: toId,
    minArmies: Math.max(1, Math.min(dice, from.armies - 1)),
  };
  checkEliminations(ctx);
  checkOutcome(ctx);
}

function occupy(ctx: Ctx, count: number): string | null {
  const { state } = ctx;
  const p = state.pendingOccupy;
  if (!p) return 'Nothing to occupy';
  const from = state.territories[p.from]!;
  const to = state.territories[p.to]!;
  const max = from.armies - 1;
  if (!Number.isInteger(count) || count < p.minArmies || count > max) {
    return `Move between ${p.minArmies} and ${max} armies`;
  }
  from.armies -= count;
  to.armies += count;
  state.pendingOccupy = null;
  checkOutcome(ctx);
  return null;
}

function fortify(ctx: Ctx, fromId: TerritoryId, toId: TerritoryId, count: number): string | null {
  const { state, map } = ctx;
  const me = currentPlayer(state).id;
  if (state.phase !== 'fortify') return 'Not in the fortify phase';
  if (state.fortified) return 'Already fortified this turn';
  const from = state.territories[fromId];
  const to = state.territories[toId];
  if (!from || !to) return 'Unknown territory';
  if (from.owner !== me || to.owner !== me) return 'Fortify between territories you own';
  if (!Number.isInteger(count) || count < 1 || count > from.armies - 1) {
    return `Move between 1 and ${from.armies - 1} armies`;
  }
  if (!connectedThroughOwned(state, map, me, fromId, toId))
    return 'No route through your territories';
  from.armies -= count;
  to.armies += count;
  state.fortified = true;
  ctx.events.push({ type: 'fortified', from: fromId, to: toId, count });
  endTurn(ctx);
  return null;
}

function endTurn(ctx: Ctx): void {
  const { state, map } = ctx;
  const me = currentPlayer(state).id;

  playerRising(map, state, me, ctx.events);
  checkEliminations(ctx);
  if (checkOutcome(ctx)) return;

  const n = state.players.length;
  let next = -1;
  let wrapped = false;
  for (let step = 1; step <= n; step++) {
    const i = (state.currentPlayerIndex + step) % n;
    if (i <= state.currentPlayerIndex) wrapped = true;
    if (!state.players[i]!.eliminated) {
      next = i;
      break;
    }
  }
  if (next < 0) throw new Error('No active players after outcome check');

  if (wrapped) {
    hordeRoundEnd(map, state, ctx.events);
    checkEliminations(ctx);
    if (checkOutcome(ctx)) return;
    state.round += 1;
  }

  state.currentPlayerIndex = next;
  state.fortified = false;
  state.pendingOccupy = null;
  state.reinforcementsPending = reinforcementsFor(state, map, state.players[next]!.id);
  setPhase(ctx, 'reinforce');
}

function checkEliminations(ctx: Ctx): void {
  const { state } = ctx;
  for (const p of state.players) {
    if (p.eliminated) continue;
    if (ownedBy(state, p.id).length > 0) continue;
    p.eliminated = true;
    state.discard.push(...p.hand);
    p.hand = [];
    ctx.events.push({ type: 'playerEliminated', playerId: p.id });
  }
}

/** Sets `outcome` and the ended phase when the game is decided. Returns true if so. */
function checkOutcome(ctx: Ctx): boolean {
  const { state } = ctx;
  if (state.outcome) return true;
  const active = activePlayers(state);
  const me: PlayerId = currentPlayer(state).id;

  const rules = rulesFor(state.config.difficulty);
  const mapSize = Object.keys(state.territories).length;

  if (active.length === 0) {
    state.outcome = { kind: 'horde', fellOnRound: state.round };
  } else if (
    state.players.length > 1 &&
    active.length === 1 &&
    ownedBy(state, active[0]!.id).length >= Math.ceil(mapSize * rules.dominationShare)
  ) {
    // Outliving everyone is not enough: you must actually hold the world.
    state.outcome = { kind: 'victory', winner: active[0]!.id, by: 'domination' };
  } else if (
    state.config.objectives.eradication &&
    totalZombies(state) === 0 &&
    ownedBy(state, 'horde').length === 0 &&
    playerById(state, me)?.eliminated === false
  ) {
    state.outcome = { kind: 'victory', winner: me, by: 'eradication' };
  }

  if (!state.outcome) return false;
  state.pendingOccupy = null;
  state.phase = 'ended';
  ctx.events.push({ type: 'gameEnded', outcome: state.outcome });
  return true;
}
